import { useMemo } from 'react';

import { useGetAvailableBatches } from '../api';
import { filterMetaData } from '../constants';
import type { ProcessedCourseWiseBatchesData, VariantInfo } from '../types';
import { processCourseWiseBatchesData, extractDayCombinations } from '../utils';

interface UseBatchDataProps {
  profileId: string;
  variantIds: VariantInfo[];
  country: string;
}

interface TagDetail {
  color: string;
  name: string;
  text: string;
}

// Helper function to get tag details for UI display
const getTagDetails = (tags: string[]): TagDetail[] => {
  return tags.map(tag => {
    const filterMeta = filterMetaData.find(filter => filter.filterName === tag);
    return filterMeta
      ? filterMeta.tagDetails
      : {
          color: 'var(--color-magical-moonlight-d3)',
          name: tag,
          text: tag,
        };
  });
};

interface UseBatchDataReturn {
  processedBatchesData: ProcessedCourseWiseBatchesData;
  dayCombinations: Record<string, string[]>;
  isLoading: boolean;
  hasError: boolean;
  loadingVariants: VariantInfo[];
  errorVariants: VariantInfo[];
  successVariants: VariantInfo[];
  progress: {
    completed: number;
    total: number;
    percentage: number;
  };
  refetch: () => void;
  getTagDetails: (tags: string[]) => TagDetail[];
}

export const useBatchData = ({
  profileId,
  variantIds,
  country,
}: UseBatchDataProps): UseBatchDataReturn => {
  // Use the multiple variants hook to handle all variants
  const {
    courseWiseBatchesData,
    isLoading,
    hasError,
    loadingVariants,
    errorVariants,
    successVariants,
    progress,
    refetch,
  } = useGetAvailableBatches({
    profileId,
    variants: variantIds,
    country,
  });

  const processedBatchesData: ProcessedCourseWiseBatchesData = useMemo(
    () => processCourseWiseBatchesData(courseWiseBatchesData ?? {}),
    [courseWiseBatchesData]
  );

  const dayCombinations: Record<string, string[]> = useMemo(
    () => extractDayCombinations(processedBatchesData),
    [processedBatchesData]
  );

  return {
    processedBatchesData,
    dayCombinations,
    isLoading,
    hasError,
    loadingVariants,
    errorVariants,
    successVariants,
    progress,
    refetch,
    getTagDetails,
  };
};
